import React from "react";
import cx from "classnames";
import {
  Button,
  ChipIcon,
  DangerIcon,
  InfoIcon,
  LockIcon,
  TickCircleIcon as SuccessIcon,
  TickIcon,
  WarningIcon,
} from "@picklebet/ui";
import { triggerHaptic } from "@/utilities/haptics";
import type { Alert as AlertType } from "@/features/betslip/types";

import * as styles from "@/features/betslip/components/parts/InlineAlert.module.scss";

type InlineAlertProps = {
  alert: AlertType | null;
  onCta?: (cta: NonNullable<AlertType["cta"]>) => void;
  /** "outcome" (default): inline band with a tail pointing at the bet row.
   *  "card": attached banner at the card's top edge — no tail, squared
   *  bottom. */
  placement?: "card" | "outcome";
};

// Explicit icon overrides come first (accepted → tick, casino session → chip,
// suspended → lock), matching legacy; everything else falls back to the
// variant's icon.
const leadIcon = (alert: AlertType) => {
  if (alert.icon === "tick") return <SuccessIcon />;
  if (alert.icon === "chip") return <ChipIcon />;
  if (alert.icon === "lock") return <LockIcon />;
  switch (alert.variant) {
    case "warning":
      return <WarningIcon />;
    case "success":
      return <SuccessIcon />;
    case "info":
      return <InfoIcon />;
    default:
      return <DangerIcon />;
  }
};

// Button has no `success` variant; map the alert's success to the brand
// primary. danger/warning/info line up one-to-one.
const ctaVariant = (variant: AlertType["variant"]) =>
  variant === "success" ? "primary" : variant;

/**
 * Alert band: icon + message on the left, an optional CTA pill on the right.
 * `placement="outcome"` sits inside the card above the bet row with a tail;
 * `placement="card"` is the attached banner at the card's top edge.
 */
export const InlineAlert = ({
  alert,
  onCta,
  placement = "outcome",
}: InlineAlertProps) => {
  if (!alert) return null;

  const handleClick = () => {
    if (!alert.cta || !onCta) return;
    triggerHaptic.click();
    onCta(alert.cta);
  };

  const animateIn = alert.cta?.action === "share";

  return (
    <div
      className={cx(styles.band, styles[alert.variant], {
        [styles.tailed]: placement === "outcome",
        [styles.card]: placement === "card",
      })}
      role="alert"
      data-variant={alert.variant}
    >
      <span className={styles.lead}>
        <span className={styles.icon}>{leadIcon(alert)}</span>
        <span className={styles.message} data-testid="alert-message">
          {alert.message}
        </span>
      </span>
      {alert.cta && (
        <Button
          variant={ctaVariant(alert.variant)}
          size="xxs"
          onClick={handleClick}
          className={styles.cta}
          data-animation-class={animateIn ? "pop" : undefined}
        >
          {alert.cta.action === "acceptOdds" && (
            <span className={styles.tick}>
              <TickIcon />
            </span>
          )}
          {alert.cta.label}
        </Button>
      )}
    </div>
  );
};
